import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Play, RotateCcw, Hand } from "lucide-react";
import { cn } from "../../lib/utils";

/* ============================================================
   FoldViewer — a tiny dependency-free 3D viewer (plain SVG).

   Renders a sewing-shape model from sewingShapes.js at fold
   parameter t: t = 0 is the flat pattern lying on the cutting
   table, t = 1 is the sewn shape, and the slider scrubs the
   fold in between. Seam edges are drawn in their seam colors
   in 3D so they can be matched against the flat pattern view.

   Rendering is intentionally simple: orthographic projection,
   painter's-algorithm depth sort, double-sided flat shading
   (fabric has two sides — during the fold you see both).
   ============================================================ */

const VIEW = 340; // square viewBox size
const LIGHT = normalize([0.45, 0.8, 0.5]);

function normalize(v) {
  const len = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / len, v[1] / len, v[2] / len];
}

// Rotate world coords by yaw (around y) then pitch (around x).
const makeRotator = (yaw, pitch) => {
  const cy = Math.cos(yaw);
  const sy = Math.sin(yaw);
  const cp = Math.cos(pitch);
  const sp = Math.sin(pitch);
  return ([x, y, z]) => {
    const X = x * cy + z * sy;
    const Z0 = -x * sy + z * cy;
    return [X, y * cp - Z0 * sp, y * sp + Z0 * cp];
  };
};

// Ease for the "sew it up" animation.
const easeInOut = (u) => (u < 0.5 ? 2 * u * u : 1 - Math.pow(-2 * u + 2, 2) / 2);

const DEFAULT_LABELS = {
  start: "flat",
  end: "sewn",
  button: "sew it up",
  aria: "Fold the pattern from flat to sewn",
  sceneAria: "3D view of the shape folding up from its flat pattern",
};

const FoldViewer = ({ model, className, labels: labelsProp }) => {
  const labels = { ...DEFAULT_LABELS, ...labelsProp };
  const [t, setT] = useState(1);
  const [view, setView] = useState({ yaw: -0.55, pitch: 0.42 });
  const dragRef = useRef(null); // { x, y, yaw, pitch } while dragging
  const animRef = useRef(null); // rAF id for the fold animation
  const [spinning, setSpinning] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return true;
    return !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  // Framing per model: measure the flat layout and the sewn shape once, then
  // blend between the two fits with t — the camera eases in as the pattern
  // contracts into the finished form, with no jumps (it's continuous in t).
  const frame = useMemo(() => {
    const radiusAt = (tt) => {
      const pts = model.positionsAt(tt);
      const c = [0, 0, 0];
      pts.forEach((p) => {
        c[0] += p[0] / pts.length;
        c[1] += p[1] / pts.length;
        c[2] += p[2] / pts.length;
      });
      let radius = 1;
      pts.forEach((p) => {
        radius = Math.max(radius, Math.hypot(p[0] - c[0], p[1] - c[1], p[2] - c[2]));
      });
      return radius;
    };
    return { flat: radiusAt(0), sewn: radiusAt(1) };
  }, [model]);

  // Animate the fold 0 -> 1 whenever the model changes (the reveal is the
  // whole point of the page), and on the replay button.
  const playFold = useCallback(() => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    const started = performance.now();
    const DUR = 2000;
    const HOLD = 350; // pause flat for a beat so the pattern registers
    const tick = (now) => {
      const raw = (now - started - HOLD) / DUR;
      if (raw < 0) {
        setT(0);
        animRef.current = requestAnimationFrame(tick);
        return;
      }
      const u = Math.min(1, raw);
      setT(easeInOut(u));
      if (u < 1) animRef.current = requestAnimationFrame(tick);
      else animRef.current = null;
    };
    animRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    playFold();
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [model, playFold]);

  // Gentle idle spin; any drag turns it off (the user has taken the wheel).
  useEffect(() => {
    if (!spinning) return undefined;
    let raf;
    let last = performance.now();
    const spin = (now) => {
      const dt = Math.min(64, now - last);
      last = now;
      if (!dragRef.current) {
        setView((v) => ({ ...v, yaw: v.yaw + dt * 0.00012 }));
      }
      raf = requestAnimationFrame(spin);
    };
    raf = requestAnimationFrame(spin);
    return () => cancelAnimationFrame(raf);
  }, [spinning]);

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { x: e.clientX, y: e.clientY, yaw: view.yaw, pitch: view.pitch };
    setSpinning(false);
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    setView({
      yaw: d.yaw + (e.clientX - d.x) * 0.008,
      pitch: Math.max(-1.4, Math.min(1.4, d.pitch + (e.clientY - d.y) * 0.008)),
    });
  };
  const onPointerUp = () => {
    dragRef.current = null;
  };

  // ---- project + sort + draw ----
  const scene = useMemo(() => {
    const rotate = makeRotator(view.yaw, view.pitch);
    const world = model.positionsAt(t);
    const pts = world.map(rotate);

    // Recenter on the current bounding box so the shape stays framed as the
    // flat layout contracts into the sewn form.
    const min = [Infinity, Infinity, Infinity];
    const max = [-Infinity, -Infinity, -Infinity];
    pts.forEach((p) => {
      for (let i = 0; i < 3; i++) {
        if (p[i] < min[i]) min[i] = p[i];
        if (p[i] > max[i]) max[i] = p[i];
      }
    });
    const c = [(min[0] + max[0]) / 2, (min[1] + max[1]) / 2, (min[2] + max[2]) / 2];
    const k = (VIEW * 0.44) / (frame.flat + (frame.sewn - frame.flat) * t);
    const sx = (p) => VIEW / 2 + (p[0] - c[0]) * k;
    const sy = (p) => VIEW / 2 - (p[1] - c[1]) * k;

    const items = [];
    model.faces.forEach((face) => {
      const vs = face.verts.map((i) => pts[i]);
      // Flat shading from the face normal (double-sided: abs).
      const ux = vs[1][0] - vs[0][0];
      const uy = vs[1][1] - vs[0][1];
      const uz = vs[1][2] - vs[0][2];
      const wx = vs[2][0] - vs[0][0];
      const wy = vs[2][1] - vs[0][1];
      const wz = vs[2][2] - vs[0][2];
      const nx = uy * wz - uz * wy;
      const ny = uz * wx - ux * wz;
      const nz = ux * wy - uy * wx;
      const nl = Math.hypot(nx, ny, nz);
      const lum =
        nl < 1e-9
          ? 0.6
          : Math.abs((nx * LIGHT[0] + ny * LIGHT[1] + nz * LIGHT[2]) / nl);
      const [hue, sat] = model.pieceTones[face.piece] || [45, 80];
      const fill = `hsl(${hue}, ${sat}%, ${46 + lum * 34}%)`;
      const depth = vs.reduce((s, p) => s + p[2], 0) / vs.length;
      items.push({
        depth,
        el: (
          <polygon
            key={`f${items.length}`}
            points={vs.map((p) => `${sx(p)},${sy(p)}`).join(" ")}
            fill={fill}
            stroke={fill}
            strokeWidth="0.6"
            strokeLinejoin="round"
          />
        ),
      });
    });

    const lineItem = (a, b, stroke, dash, width, keyPrefix, idx) => {
      const pa = pts[a];
      const pb = pts[b];
      items.push({
        // Nudge lines toward the viewer so they sit on top of their own face.
        depth: (pa[2] + pb[2]) / 2 + 0.35,
        el: (
          <line
            key={`${keyPrefix}${idx}`}
            x1={sx(pa)}
            y1={sy(pa)}
            x2={sx(pb)}
            y2={sy(pb)}
            stroke={stroke}
            strokeWidth={width}
            strokeDasharray={dash}
            strokeLinecap="round"
          />
        ),
      });
    };

    model.folds.forEach(([a, b], i) =>
      lineItem(a, b, "rgba(120,120,140,0.55)", "4 4", 1.4, "fo", i)
    );
    model.seams.forEach((seam, si) =>
      seam.edges.forEach(([a, b], i) => lineItem(a, b, seam.color, null, 2.6, `s${si}-`, i))
    );

    // Attachment markers (composites): a dot rides each joined part; the two
    // dots of a joint converge as the assembly slider closes.
    (model.markers || []).forEach((marker, i) => {
      const p = pts[marker.vert];
      items.push({
        depth: p[2] + 0.6,
        el: (
          <circle
            key={`m${i}`}
            cx={sx(p)}
            cy={sy(p)}
            r="5"
            fill={marker.color}
            stroke="rgba(255,255,255,0.85)"
            strokeWidth="1.6"
          />
        ),
      });
    });

    items.sort((p, q) => p.depth - q.depth);
    return items.map((it) => it.el);
  }, [model, t, view, frame]);

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <div className="relative overflow-hidden rounded-xl border bg-gradient-to-b from-background to-secondary/40">
        <svg
          viewBox={`0 0 ${VIEW} ${VIEW}`}
          className="block w-full cursor-grab touch-none select-none active:cursor-grabbing"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          role="img"
          aria-label={labels.sceneAria}
        >
          {scene}
        </svg>
        <div className="pointer-events-none absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-background/70 px-2.5 py-1 text-xs text-muted-foreground backdrop-blur">
          <Hand className="h-3 w-3" />
          drag to rotate
        </div>
        <button
          type="button"
          onClick={() => setSpinning((s) => !s)}
          className={cn(
            "absolute right-3 top-3 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs backdrop-blur transition-colors",
            spinning
              ? "border-primary/50 bg-primary/10 text-primary"
              : "border-border bg-background/70 text-muted-foreground hover:text-foreground"
          )}
        >
          <RotateCcw className="h-3 w-3" />
          spin
        </button>
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={playFold}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-input bg-background px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
          title="Replay the animation"
        >
          <Play className="h-3.5 w-3.5" />
          {labels.button}
        </button>
        <div className="flex w-full items-center gap-2 text-[0.65rem] uppercase tracking-wide text-muted-foreground">
          <span className="shrink-0">{labels.start}</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.005"
            value={t}
            onChange={(e) => {
              if (animRef.current) {
                cancelAnimationFrame(animRef.current);
                animRef.current = null;
              }
              setT(Number(e.target.value));
            }}
            className="w-full accent-primary"
            aria-label={labels.aria}
          />
          <span className="shrink-0">{labels.end}</span>
        </div>
      </div>
    </div>
  );
};

export default FoldViewer;
